import {Injectable} from '@angular/core';
import {firstValueFrom} from 'rxjs';

import {ApiConfig} from '../../config/api-config';
import {Session} from '../../../components/session/session';
import {AdnApiClientService} from './adn-api-client.service';

function b64urlToBytes(b64u: string): Uint8Array {
  const pad = '='.repeat((4 - (b64u.length % 4)) % 4);
  const b64 = (b64u + pad).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    bytes[i] = bin.charCodeAt(i);
  }
  return bytes;
}

function bufToB64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let value = '';
  for (let i = 0; i < bytes.length; i++) {
    value += String.fromCharCode(bytes[i]);
  }
  return btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function prepareCreationOptions(publicKey: any): PublicKeyCredentialCreationOptions {
  return {
    ...publicKey,
    challenge: b64urlToBytes(publicKey.challenge),
    user: {
      ...publicKey.user,
      id: b64urlToBytes(publicKey.user.id)
    },
    excludeCredentials: Array.isArray(publicKey.excludeCredentials)
      ? publicKey.excludeCredentials.map((credential: any) => ({
        ...credential,
        id: b64urlToBytes(credential.id)
      }))
      : undefined
  };
}

function prepareRequestOptions(publicKey: any): PublicKeyCredentialRequestOptions {
  return {
    ...publicKey,
    challenge: b64urlToBytes(publicKey.challenge),
    allowCredentials: Array.isArray(publicKey.allowCredentials)
      ? publicKey.allowCredentials.map((credential: any) => ({
        ...credential,
        id: b64urlToBytes(credential.id)
      }))
      : undefined
  };
}

interface AssertionResponseWithTransports extends AuthenticatorAssertionResponse {
  getTransports?: () => string[];
}

@Injectable({providedIn: 'root'})
export class WebAuthnService {
  private readonly url = this.apiConfig.getVersionedUri('webauthn');

  constructor(private readonly client: AdnApiClientService, private readonly apiConfig: ApiConfig) {
  }

  async loginWithPasskey(credentials: {username: any; masqUsername: any}) {
    const optionsRes: any = await this.send({
      method: 'POST',
      url: this.url + '/authentication/options',
      body: {username: credentials.username}
    }).catch((err: any) => {
      if (err.data) {
        throw err.data;
      }
    });
    const publicKeyRes = optionsRes.publicKey;
    const requestId = optionsRes.requestId;
    const publicKey = prepareRequestOptions(publicKeyRes);
    const credAny = await navigator.credentials.get({publicKey}).catch((err: any) => {
      throw err;
    });
    if (!credAny) {
      throw {uiResponse: 1, text: 'No relevant Passkeys were found on your device.'};
    }

    const cred = credAny as PublicKeyCredential;
    const resp = cred.response as AssertionResponseWithTransports;
    const transports = resp.getTransports ? resp.getTransports() : [];
    const payload = {
      requestId,
      masqueradeAsUser: credentials.masqUsername,
      credential: {
        id: cred.id,
        rawId: bufToB64url(cred.rawId),
        type: cred.type,
        response: {
          clientDataJSON: bufToB64url(resp.clientDataJSON),
          authenticatorData: bufToB64url(resp.authenticatorData),
          signature: bufToB64url(resp.signature),
          userHandle: resp.userHandle ? bufToB64url(resp.userHandle) : null
        },
        transports
      }
    };

    const data: any = await this.send({
      method: 'POST',
      url: `${this.url}/authentication/result`,
      params: {requestId},
      body: payload
    }).catch((err: any) => {
      if (err.data) {
        throw err.data;
      }
    });

    return Session.obtain().init(
      data.scope,
      data.access_token,
      data.refresh_token,
      data.username,
      data.expires_in,
      data.username,
      undefined,
      data.auth_type
    );
  }

  existingCredentials() {
    return this.send({
      method: 'GET',
      url: this.url + '/credentials',
      params: {includeDeleted: true}
    }).catch(() => null);
  }

  deleteCredential(id: string) {
    return this.send({method: 'POST', url: this.url + '/credential/delete/' + id, body: {}})
      .catch(() => null);
  }

  updateDescription(id: string, description: string) {
    return this.send({
      method: 'POST',
      url: this.url + '/credential/description/' + id,
      body: {description}
    }).catch(() => null);
  }

  restoreCredential(id: string) {
    return this.send({method: 'POST', url: this.url + '/credential/restore/' + id, body: {}})
      .catch(() => null);
  }

  async registerCredentialsOptions() {
    const response = await this.send({method: 'POST', url: this.url + '/registration/options', body: {}});
    const requestId = response.requestId;
    const publicKey = prepareCreationOptions(response.publicKey);
    const cred = await navigator.credentials.create({publicKey}).catch((err) => {
      if (err.name === 'InvalidStateError') {
        throw {text: 'Passkey is already registered'};
      }
      return null;
    });

    if (!cred) {
      return null;
    }
    const pkc = cred as PublicKeyCredential;
    const att = pkc.response as AuthenticatorAttestationResponse;
    const transports = att.getTransports ? att.getTransports() : [];
    return {
      requestId,
      credential: {
        id: cred.id,
        rawId: bufToB64url(pkc.rawId),
        type: cred.type,
        response: {
          clientDataJSON: bufToB64url(att.clientDataJSON),
          attestationObject: bufToB64url(att.attestationObject)
        },
        transports
      }
    };
  }

  registerCredentials(twoFactorAuthCode: string, description: string, payload: any) {
    payload.twoFactorAuthCode = twoFactorAuthCode;
    payload.description = description;
    return this.send({
      method: 'POST',
      url: `${this.url}/registration/result`,
      body: payload
    }).then(() => true);
  }

  private send(config: any): Promise<any> {
    return firstValueFrom(this.client.request(config));
  }
}
